import { Link } from 'react-router-dom';
import PublicLayout, { SEOHead } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { Zap, UserCheck, Mic, ArrowRight, CheckCircle, Shield, BarChart3, Clock } from 'lucide-react';

export default function HowItWorksPage() {
  return (
    <PublicLayout>
      <SEOHead title="How It Works | Get Connected to an Interpreter in Seconds | Interpreter Services" description="Learn how Interpreter Services delivers smart interpreting — AI when you want it, human when you need it. Audio-first, real-time, no scheduling required." canonical="https://interpreterservices.us/how-it-works" />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">How Interpreter Services Works</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">Smart interpreting, your way. AI when you want it. Human when you need it. No scheduling, no delays.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Smart Interpreting Workflow */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Smart Interpreting Workflow</h2>
          <p className="text-slate-700 leading-relaxed mb-6">Interpreter Services puts the choice in your hands. Start an AI interpreting session instantly — no appointment, no hold queue, no dispatcher — or connect directly to a professional human interpreter when the situation calls for it. Instant AI. Expert humans. Total choice.</p>
          <p className="text-slate-700 leading-relaxed mb-8">This is fundamentally different from traditional interpreting, which requires scheduling, availability checks, and often significant wait times. With Interpreter Services, the right interpreter is always available, always ready, in over 250 languages.</p>
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            {[
              { step: '1', icon: Mic, title: 'Start a Session', desc: 'Select your target language and begin. Press to start speaking. The AI interpreter activates instantly. Press to stop the interpreting.' },
              { step: '2', icon: Zap, title: 'Speak Naturally', desc: 'Real-time audio interpretation. Speak at your natural pace — the AI keeps up.' },
              { step: '3', icon: UserCheck, title: 'Escalate If Needed', desc: 'One tap to connect to a professional human interpreter. Seamless handoff.' },
            ].map(s => (
              <div key={s.step} className="bg-slate-50 rounded-xl p-6 border text-center">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center mx-auto mb-4">{s.step}</div>
                <h3 className="font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Audio-First Approach */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Audio-First Approach</h2>
          <p className="text-slate-700 leading-relaxed mb-4">Interpreting is fundamentally a spoken communication service. Unlike text-based translation tools, Interpreter Services is built for audio-first interaction — designed for phone calls, in-person conversations, and live meetings where speaking and listening are the primary mode of communication.</p>
          <p className="text-slate-700 leading-relaxed">This makes it suitable for field environments such as policing, immigration intake, hospital emergency departments, and courtroom proceedings where operators need hands-free, real-time language access.</p>
        </section>

        {/* Human Escalation */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Human Escalation Workflow</h2>
          <p className="text-slate-700 leading-relaxed mb-6">AI interpreting handles the majority of interactions — fast, cost-effective, and always available. But some situations require a professional human interpreter: legal proceedings, medical consent, complex emotional conversations, or when the AI indicates reduced confidence.</p>
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-6">
            <p className="text-slate-800 font-medium">When you escalate, the human interpreter joins the session with context from the AI interaction. There is no need to repeat information. The transition is seamless.</p>
          </div>
          <ul className="space-y-3">
            {[
              'User initiates escalation with one action',
              'Professional human interpreter connects within seconds for supported languages',
              'Session context is preserved during handoff',
              'Human interpreter follows the same compliance and audit controls',
              'Session recording and reporting continues uninterrupted',
            ].map(item => (
              <li key={item} className="flex items-start gap-3 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />{item}</li>
            ))}
          </ul>
        </section>

        {/* AI vs Human */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">AI Session vs Human Escalation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 rounded-xl p-6 border">
              <h3 className="font-bold mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-blue-600" /> AI Interpreter</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>Instant connection, no wait</li>
                <li>250+ languages available</li>
                <li>Usage-based pricing</li>
                <li>Ideal for routine interactions</li>
                <li>Always available, 24/7</li>
              </ul>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h3 className="font-bold mb-4 flex items-center gap-2"><UserCheck className="w-5 h-5 text-emerald-600" /> Human Escalation</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>Professional human interpreters</li>
                <li>Best for high-risk or complex situations</li>
                <li>Premium pricing per session</li>
                <li>SLA-backed for supported languages</li>
                <li>Seamless handoff from AI session</li>
              </ul>
            </div>
          </div>
        </section>

        {/* User Control */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">User Control Over Escalation</h2>
          <p className="text-slate-700 leading-relaxed mb-6">Users remain in full control of escalation decisions. The system may suggest escalation when it detects reduced accuracy or sensitive content, but the user decides whether to proceed with AI or switch to a human interpreter.</p>
          <p className="text-slate-700 leading-relaxed">Organizations can configure escalation policies — such as automatic escalation for specific language pairs, session types, or compliance requirements — through the enterprise admin panel.</p>
        </section>

        {/* Why Faster */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Why This Is Faster Than Traditional Scheduling</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Clock, title: 'No scheduling required', desc: 'Traditional interpreting often requires 24-48 hours advance booking. Interpreter Services starts instantly.' },
              { icon: Zap, title: 'No hold queues', desc: 'Skip the dispatcher. Select your language and the AI interpreter is ready immediately.' },
              { icon: Shield, title: 'Always available', desc: '24/7/365 availability in 250+ languages. No "after-hours" gaps.' },
              { icon: BarChart3, title: 'Lower cost for routine use', desc: 'AI interpreting at a fraction of the cost of human interpreters for everyday interactions.' },
            ].map(r => (
              <div key={r.title} className="flex items-start gap-4 p-5 bg-slate-50 rounded-lg">
                <r.icon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div><h4 className="font-bold text-sm mb-1">{r.title}</h4><p className="text-sm text-slate-600">{r.desc}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* Suitable Use Cases */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Suitable Use Cases</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Field interviews and policing', 'Immigration intake', 'Hospital patient intake',
              'Corrections intake and processing', 'Customer service calls', 'Government benefits administration',
              'School parent conferences', 'Legal client intake', 'Emergency dispatch',
            ].map(uc => (
              <div key={uc} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-700">
                <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />{uc}
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* CTA */}
      <section className="py-16 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-10">Start Free</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 px-10">Book Demo</Button></Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
